// packages/web/src/components/MetricsView.tsx — misma lógica (getMetrics + getConcordance, rango en días).
// Cambios: rango como control segmentado, cifras agrupadas (uso / costo / tokens), tabla con números
// alineados y tabulares, gráfico diario como barras verticales con su valor.
import { useEffect, useState } from 'react';
import { Loader2, ThumbsUp, Pencil, RotateCcw } from 'lucide-react';
import { api, type MetricsSummary } from '../api';
import { DICTAMEN_LABEL, type ConcordanceMetrics } from '../types';
import { Alert, Button, Card, PageHeader, Stat, TABLE } from './ui';

interface MetricsViewProps { siteId: string; onClose: () => void }
const RANGES = [7, 30, 90, 180] as const;
const nf = (n: number) => n.toLocaleString('es-CL');

export function MetricsView({ siteId, onClose }: MetricsViewProps) {
  const [data, setData] = useState<MetricsSummary | null>(null);
  const [concordance, setConcordance] = useState<ConcordanceMetrics | null>(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    Promise.all([api.getMetrics(siteId, days), api.getConcordance(siteId, days).catch(() => null)])
      .then(([m, c]) => { if (cancelled) return; setData(m); if (c) setConcordance(c); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [siteId, days]);

  const costPerPilot = data && data.totals.pilots_closed > 0 ? data.totals.est_usd / data.totals.pilots_closed : null;
  const maxDay = data ? Math.max(1, ...data.by_day.map((d) => d.est_usd)) : 1;

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <PageHeader
        back={{ label: 'Cerrar', onClick: onClose }}
        title="Métricas y costos"
        subtitle={<>Llamadas a modelos y sesiones de la sede <span className="font-mono">{siteId}</span>.</>}
        actions={
          <div role="radiogroup" aria-label="Rango" className="inline-flex rounded-lg border border-slate-200 bg-white p-1 gap-1">
            {RANGES.map((r) => (
              <button key={r} type="button" role="radio" aria-checked={days === r} onClick={() => setDays(r)}
                className={`rounded-md px-3 py-1.5 text-sm ${days === r ? 'bg-marzano-600 text-white font-semibold' : 'text-slate-700 hover:bg-slate-100'} focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-amber-500`}>
                {r} días
              </button>
            ))}
          </div>
        }
      />

      {loading && <p className="flex items-center gap-2 text-sm text-slate-700"><Loader2 aria-hidden className="w-4 h-4 animate-spin" />Cargando…</p>}
      {error && <Alert kind="error">{error}</Alert>}

      {data && !loading && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Stat label="Costo estimado" value={`US$ ${data.totals.est_usd.toFixed(2)}`} hint={costPerPilot !== null ? `US$ ${costPerPilot.toFixed(3)} por evaluación cerrada` : undefined} />
            <Stat label="Evaluaciones cerradas" value={nf(data.totals.pilots_closed)} hint={`de ${nf(data.totals.sessions)} sesiones`} />
            <Stat label="Llamadas a modelos" value={nf(data.totals.llm_calls)} />
            <Stat label="Minutos de audio" value={data.totals.whisper_minutes.toFixed(1)} hint="transcripción" />
          </div>
          <p className="text-[13px] text-slate-600 -mt-3">
            Tokens: <strong className="text-slate-900 tabular-nums">{nf(data.totals.input_tokens)}</strong> de entrada · <strong className="text-slate-900 tabular-nums">{nf(data.totals.output_tokens)}</strong> de salida.
          </p>
        </>
      )}

      {concordance && concordance.total_reviewed > 0 && <ConcordanceCard data={concordance} />}

      {data && !loading && (
        <>
          <Card title="Costo por día">
            {data.by_day.length === 0 ? <p className="text-sm text-slate-600 text-center py-4">Sin datos en el rango.</p> : (
              <div className="overflow-x-auto">
                <div className="flex items-end gap-1 h-40 min-w-max pt-5" role="img" aria-label={`Costo diario: ${data.by_day.map((d) => `${d.day} US$ ${d.est_usd.toFixed(3)}`).join(', ')}`}>
                  {data.by_day.map((d) => (
                    <div key={d.day} className="group relative flex flex-col items-center justify-end h-full w-5" title={`${d.day} · ${d.calls} llamadas · US$ ${d.est_usd.toFixed(3)}`}>
                      <div className="w-full rounded-t bg-marzano-500 group-hover:bg-marzano-700" style={{ height: `${Math.max(2, (d.est_usd / maxDay) * 100)}%` }} />
                    </div>
                  ))}
                </div>
                <div className="flex justify-between text-xs text-slate-600 mt-1.5 min-w-max gap-8">
                  <span>{data.by_day[0]?.day}</span><span>Máx. US$ {maxDay.toFixed(3)}</span><span>{data.by_day[data.by_day.length - 1]?.day}</span>
                </div>
              </div>
            )}
          </Card>

          <section aria-labelledby="bymodel-t" className="space-y-3">
            <h2 id="bymodel-t" className="text-base font-semibold text-slate-900">Por modelo y tipo de llamada</h2>
            <div className={TABLE.wrap}>
              <table className={TABLE.table + ' tabular-nums'}>
                <thead className={TABLE.thead}>
                  <tr>
                    <th className={TABLE.th}>Modelo</th><th className={TABLE.th}>Tipo</th>
                    <th className={TABLE.th + ' text-right'}>Llamadas</th><th className={TABLE.th + ' text-right'}>Errores</th>
                    <th className={TABLE.th + ' text-right'}>Tokens in</th><th className={TABLE.th + ' text-right'}>Tokens out</th>
                    <th className={TABLE.th + ' text-right'}>Lat. prom.</th><th className={TABLE.th + ' text-right'}>Lat. p95</th>
                    <th className={TABLE.th + ' text-right'}>US$</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_model.map((m, i) => (
                    <tr key={i} className={TABLE.tr}>
                      <td className={TABLE.td + ' font-mono text-[13px]'}>{m.model}</td>
                      <td className={TABLE.td + ' text-slate-700'}>{m.call_type}</td>
                      <td className={TABLE.td + ' text-right'}>{nf(m.calls)}</td>
                      <td className={TABLE.td + ' text-right'}>{m.failures > 0 ? <span className="font-semibold text-red-700">{m.failures} ({Math.round((m.failures / Math.max(1, m.calls)) * 100)}%)</span> : <span className="text-slate-500">—</span>}</td>
                      <td className={TABLE.td + ' text-right'}>{nf(m.input_tokens)}</td>
                      <td className={TABLE.td + ' text-right'}>{nf(m.output_tokens)}</td>
                      <td className={TABLE.td + ' text-right'}>{nf(m.avg_latency_ms)} ms</td>
                      <td className={TABLE.td + ' text-right ' + (m.p95_latency_ms > 10000 ? 'text-amber-800 font-semibold' : '')}>{nf(m.p95_latency_ms)} ms</td>
                      <td className={TABLE.td + ' text-right font-semibold'}>{m.est_usd.toFixed(4)}</td>
                    </tr>
                  ))}
                  {data.by_model.length === 0 && <tr><td colSpan={9} className="text-center py-6 text-slate-600">Sin llamadas en el rango.</td></tr>}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
      {!loading && !data && !error && <Button variant="secondary" onClick={onClose}>Cerrar</Button>}
    </div>
  );
}

function ConcordanceCard({ data }: { data: ConcordanceMetrics }) {
  const pct = data.concordance_pct ?? 0;
  const reached = pct >= 85;
  return (
    <Card title="Concordancia IA–docente" description="Revisiones docentes que aceptaron el reporte de la IA. Objetivo del piloto: 85 % o más.">
      <div className="grid grid-cols-1 md:grid-cols-[1.2fr_1fr_1fr_1fr] gap-3">
        <div className="rounded-lg border border-slate-200 px-4 py-3">
          <div className="text-[13px] text-slate-600">Concordancia</div>
          <div className={`text-[32px] font-bold tabular-nums ${reached ? 'text-emerald-800' : 'text-amber-800'}`}>{data.concordance_pct !== null ? `${pct.toFixed(0)}%` : '—'}</div>
          <div className="text-[13px] text-slate-600">{reached ? '✓ Cumple el objetivo' : '◐ Bajo el objetivo'} · {data.total_reviewed} revisiones</div>
        </div>
        <MiniStat icon={<ThumbsUp aria-hidden className="w-4 h-4" />} label="Aceptadas" value={data.accepted} cls="text-emerald-800" />
        <MiniStat icon={<Pencil aria-hidden className="w-4 h-4" />} label="Modificadas" value={data.modified} cls="text-amber-800" />
        <MiniStat icon={<RotateCcw aria-hidden className="w-4 h-4" />} label="Re-evaluar" value={data.rerun_requested} cls="text-red-700" />
      </div>
      <div>
        <div className="relative h-2.5 bg-slate-100 rounded-full" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct)} aria-label="Concordancia">
          <div className={`h-2.5 rounded-full ${reached ? 'bg-emerald-600' : pct >= 70 ? 'bg-amber-500' : 'bg-red-600'}`} style={{ width: `${Math.min(100, pct)}%` }} />
          <span aria-hidden className="absolute -top-1 bottom-[-4px] w-0.5 bg-marzano-600" style={{ left: '85%' }} />
        </div>
        <div className="relative text-xs text-slate-600 mt-1 h-4"><span>0%</span><span className="absolute -translate-x-1/2 font-semibold text-marzano-700" style={{ left: '85%' }}>objetivo 85%</span><span className="absolute right-0">100%</span></div>
      </div>
      {data.by_state.length > 0 && (
        <div className="pt-3 border-t border-slate-100">
          <h3 className="text-[13px] font-semibold text-slate-700 mb-2">Por estado del reporte IA</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {data.by_state.map((s) => (
              <div key={s.state} className="rounded-lg border border-slate-200 px-3 py-2">
                <div className="text-[13px] text-slate-700">{DICTAMEN_LABEL[s.state]}</div>
                <div className="text-lg font-semibold tabular-nums">{s.concordance_pct !== null ? `${s.concordance_pct.toFixed(0)}%` : '—'}</div>
                <div className="text-xs text-slate-600 tabular-nums">{s.accepted} de {s.total}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

function MiniStat({ icon, label, value, cls }: { icon: React.ReactNode; label: string; value: number; cls: string }) {
  return (
    <div className="rounded-lg border border-slate-200 px-4 py-3">
      <div className={`flex items-center gap-1.5 text-[13px] font-semibold ${cls}`}>{icon}{label}</div>
      <div className="text-2xl font-semibold tabular-nums text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}
